/**
 * EnvironmentDetailPage — /agentes/:id OR /ambientes/:id
 * Painel de gerenciamento completo estilo Hostinger VPS
 */
import { h } from 'preact';
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../api/client';
import { showToast } from '../components/Toast';
import { isLoggedIn, authChecked, authLoading } from '../stores/auth.store';
import { LoginPage } from './Login';

import { T } from '../styles/tokens';
import { Button } from '../components/ui';
// ── Theme ──
const C = {
  bg: T.bg, card: T.bgInput, cardHover: '#1a2235', border: T.border,
  green: T.success, yellow: T.warning, red: T.danger, blue: T.primary, purple: T.purple,
  text: T.text, secondary: T.textSecondary, muted: T.textMuted,
  darkBg: '#080c15', gradient: 'linear-gradient(135deg, #2F6BFF, #6D4CFF)',
};

const statusMap: Record<string, { color: string; label: string; icon: string }> = {
  running: { color: C.green, label: 'Em Atividade', icon: '' },
  creating: { color: C.yellow, label: 'Criando...', icon: '' },
  pending: { color: C.yellow, label: 'Pendente', icon: '' },
  restarting: { color: C.yellow, label: 'Reiniciando', icon: '' },
  stopped: { color: C.muted, label: 'Parado', icon: '' },
  error: { color: C.red, label: 'Erro', icon: '' },
  failed: { color: C.red, label: 'Falhou', icon: '' },
  deleted: { color: C.muted, label: 'Deletado', icon: '' },
};

function getStatus(s: string) { return statusMap[s] || statusMap['pending']; }

// ── Mini Components ──
function BackIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>;
}

function CopyIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>;
}

function DownloadIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
}

function Sparkline({ percent, color }: { percent: number; color: string }) {
  // Generate fake sparkline bars based on percent
  const bars = 8;
  const heights = Array.from({ length: bars }, (_, i) => {
    const base = Math.max(10, percent - 20 + Math.random() * 40);
    return Math.min(100, Math.max(5, base + (i === bars - 1 ? percent - base : 0)));
  });
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 32, marginTop: 8 }}>
      {heights.map((h, i) => (
        <div key={i} style={{
          width: 6, borderRadius: 2,
          height: `${h}%`,
          background: i === bars - 1 ? color : `${color}66`,
          transition: 'height 0.5s ease',
        }} />
      ))}
    </div>
  );
}

function DonutChart({ percent, color, size = 56 }: { percent: number; color: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `conic-gradient(${color} 0% ${percent}%, ${C.border} ${percent}% 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '8px auto 0',
    }}>
      <div style={{
        width: size - 14, height: size - 14, borderRadius: '50%', background: C.card,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color: C.text,
      }}>
        {percent}%
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub, type, percent, color }: {
  label: string; value: string; sub?: string; type?: 'sparkline' | 'donut' | 'text'; percent?: number; color?: string;
}) {
  const c = color || C.blue;
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16,
      minWidth: 0, textAlign: 'center',
    }}>
      <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      {type === 'sparkline' && percent !== undefined ? (
        <>
          <Sparkline percent={percent} color={c} />
          <div style={{ fontSize: 20, fontWeight: 800, color: c, marginTop: 4 }}>{value}</div>
        </>
      ) : type === 'donut' && percent !== undefined ? (
        <>
          <DonutChart percent={percent} color={c} />
          <div style={{ fontSize: 12, color: C.secondary, marginTop: 4 }}>{sub}</div>
        </>
      ) : (
        <div style={{ fontSize: 24, fontWeight: 800, color: c, marginTop: 8 }}>{value}</div>
      )}
      {type !== 'donut' && sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ActionCard({ icon, label, sub, onClick }: { icon: string; label: string; sub: string; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20,
        cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s',
      }}
      onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.background = C.cardHover; }}
      onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.card; }}
    >
      <div style={{ fontSize: 28 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginTop: 6 }}>{label}</div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}22` }}>
      <span style={{ fontSize: 13, color: C.muted }}>{label}</span>
      <span style={{ fontSize: 13, color: C.text, fontWeight: 600, fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit', maxWidth: '60%', textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

function ConfirmModal({ title, message, onConfirm, onCancel, danger }: {
  title: string; message: string; onConfirm: () => void; onCancel: () => void; danger?: boolean;
}) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 32, maxWidth: 400, width: '90%' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: danger ? C.red : C.text }}>{title}</h3>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: C.secondary, lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onCancel}>Cancelar</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>{danger ? 'Deletar Permanentemente' : 'Confirmar'}</Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ──
export function EnvironmentDetailPage({ id }: { id?: string; path?: string }) {
  if (!authChecked.value || authLoading.value) return null;
  if (!isLoggedIn.value) return <LoginPage />;

  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);

  // Check plan access
  useEffect(() => {
    api.get<any>('/subscription/me').then(res => {
      const slug = res?.subscription?.plan_id || res?.subscription?.plan || res?.plan?.slug || 'free';
      setUserPlan(slug.toLowerCase());
    }).catch(() => setUserPlan('free'));
  }, []);

  // Redirect non-enterprise users
  useEffect(() => {
    if (userPlan !== null && userPlan !== 'enterprise') {
      route('/upgrade');
    }
  }, [userPlan]);
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [confirmAction, setConfirmAction] = useState<null | 'restart' | 'stop' | 'delete'>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [tokenVisible, setTokenVisible] = useState(false);
  const logsRef = useRef<HTMLPreElement>(null);
  const intervalRef = useRef<any>(null);

  // Resolve workspace ID: strip "ws-" prefix if present
  const wsId = id?.startsWith('ws-') ? id.slice(3) : id;

  const fetchDetails = useCallback(async () => {
    if (!wsId) return;
    try {
      const res = await api.get<any>(`/cloud/workspaces/${wsId}/details`);
      setDetails(res);
    } catch (e: any) {
      // Try unified fallback
      try {
        const res2 = await api.get<{ machines: any[] }>('/cloud/workspaces/unified');
        const m = (res2.machines || []).find((m: any) => m.id === id || m.workspace_id === wsId);
        if (m) setDetails({ workspace: { id: m.workspace_id || wsId, name: m.name, image: m.image, plan: m.plan_name, status: m.online ? 'running' : 'offline', created_at: m.created_at, workspace_url: m.workspace_url }, k8s: null, metrics: null, plan_details: null });
      } catch {}
    }
    setLoading(false);
  }, [wsId, id]);

  const fetchLogs = useCallback(async () => {
    if (!wsId) return;
    setLogsLoading(true);
    try {
      const res = await api.get<any>(`/cloud/workspaces/${wsId}/logs?tail=200`);
      setLogs(res.logs || 'Sem logs disponíveis');
      setTimeout(() => { if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight; }, 100);
    } catch { setLogs('Erro ao carregar logs'); }
    setLogsLoading(false);
  }, [wsId]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchDetails, 15000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchDetails]);

  const handleAction = async (action: 'restart' | 'stop' | 'delete') => {
    if (!wsId) return;
    setActionLoading(true);
    try {
      if (action === 'restart') {
        await api.post(`/cloud/workspaces/${wsId}/restart`, {});
        showToast('Reiniciando ambiente...', 'success');
      } else if (action === 'stop') {
        await api.post(`/cloud/workspaces/${wsId}/stop`, {});
        showToast('Parando ambiente...', 'success');
      } else if (action === 'delete') {
        await api.delete(`/cloud/workspaces/${wsId}`);
        showToast('Ambiente deletado', 'success');
        route('/ambientes');
        return;
      }
      setConfirmAction(null);
      setTimeout(fetchDetails, 2000);
    } catch (e: any) {
      showToast(e.message || `Erro ao ${action}`, 'error');
    }
    setActionLoading(false);
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => showToast('URL copiada!', 'success'));
  };

  const downloadLogs = () => {
    const blob = new Blob([logs], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `logs-${wsId}-${Date.now()}.txt`;
    a.click();
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
        {[200, '100%', '100%'].map((w, i) => (
          <div key={i} style={{ width: w, height: i === 0 ? 24 : 120, background: T.bgCard, borderRadius: 12, marginBottom: 16 }} />
        ))}
      </div>
    );
  }

  if (!details) {
    return (
      <div style={{ padding: 24, maxWidth: 960, margin: '0 auto', textAlign: 'center', paddingTop: 80 }}>
        <p style={{ color: C.muted, fontSize: 16 }}>Ambiente não encontrado</p>
        <Button variant="primary" onClick={() => route('/ambientes')} style={{ marginTop: 16 }}>Voltar</Button>
      </div>
    );
  }

  const ws = details.workspace || {};
  const k8s = details.k8s || {};
  const metrics = details.metrics || {};
  const planD = details.plan_details || {};
  const pod = k8s.pod || {};
  const events = k8s.events || [];
  const st = getStatus(ws.status);
  const cpuP = metrics.cpu?.percentUsed || 0;
  const memP = metrics.memory?.percentUsed || 0;

  return (
    <div style={{ padding: '16px 24px', maxWidth: 960, margin: '0 auto' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: C.muted, fontSize: 13 }} onClick={() => route('/ambientes')}>
          <BackIcon />
          <span>Voltar</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.muted, cursor: 'pointer' }}>
            <input type="checkbox" checked={autoRefresh} onChange={(e: any) => setAutoRefresh(e.target.checked)} style={{ accentColor: C.blue }} />
            Auto-refresh
          </label>
          {ws.workspace_url && (
            <Button variant="primary" size="sm" onClick={() => window.open(`/agents/${id}/terminal`, '_blank')}>Terminal</Button>
          )}
        </div>
      </div>

      {/* ── Image + Name ── */}
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>{ws.name || 'Ambiente'}</h1>
        <div style={{ fontSize: 13, color: C.muted, fontFamily: "'JetBrains Mono', monospace", marginTop: 4 }}>{ws.image || ''}</div>
      </div>

      {/* ── Info Bar ── */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%', background: st.color,
              boxShadow: ws.status === 'running' ? `0 0 10px ${st.color}` : 'none',
            }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: st.color }}>{st.label}</span>
          </div>
          <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: `${C.purple}22`, color: C.purple }}>
            {planD.name || ws.plan || 'Starter'}
          </span>
          {ws.workspace_url && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <a href={ws.workspace_url} target="_blank" rel="noopener" style={{ color: C.blue, fontSize: 13, textDecoration: 'none' }}>
                {ws.workspace_url}
              </a>
              <span style={{ cursor: 'pointer', color: C.muted }} onClick={() => copyUrl(ws.workspace_url)} title="Copiar URL">
                <CopyIcon />
              </span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="ghost" size="sm" onClick={() => setConfirmAction('restart')}>Reiniciar</Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirmAction('stop')} style={{ color: C.yellow }}>Parar</Button>
          <Button variant="danger" size="sm" onClick={() => setConfirmAction('delete')}>Deletar</Button>
        </div>
      </div>

      {/* ── Metrics Grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        <MetricCard label="CPU" value={`${cpuP}%`} sub={metrics.cpu ? `${metrics.cpu.usage} / ${metrics.cpu.limit}` : ''} type="sparkline" percent={cpuP} color={cpuP > 80 ? C.red : cpuP > 50 ? C.yellow : C.green} />
        <MetricCard label="Memória" value={`${memP}%`} sub={metrics.memory ? `${metrics.memory.usage} / ${metrics.memory.limit}` : ''} type="sparkline" percent={memP} color={memP > 80 ? C.red : memP > 50 ? C.yellow : C.blue} />
        <MetricCard label="Disco" value={`${planD.storage || '5GB'}`} sub={planD.storage || '5GB'} type="donut" percent={24} color={C.purple} />
        <MetricCard label="Restarts" value={`${pod.restartCount || 0}`} color={pod.restartCount > 5 ? C.red : C.green} />
        <MetricCard label="Uptime" value={k8s.uptime || '—'} color={C.blue} />
        <MetricCard label="Réplicas" value={`${k8s.deployment?.readyReplicas || 0}/${k8s.deployment?.replicas || 0}`} color={C.green} />
      </div>

      {/* ── Quick Actions ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        <ActionCard icon="" label="Terminal" sub="Abrir terminal" onClick={() => window.open(`/agents/${id}/terminal`, '_blank')} />
        <ActionCard icon="" label="Logs" sub="Ver logs" onClick={() => { setLogsExpanded(!logsExpanded); if (!logs) fetchLogs(); }} />
        <ActionCard icon="" label="Env Vars" sub="Gerenciar" onClick={() => showToast('Em breve', 'info')} />
        <ActionCard icon="" label="Eventos" sub={`${events.length} eventos`} onClick={() => setEventsExpanded(!eventsExpanded)} />
      </div>

      {/* ── Details Grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 20 }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: C.text }}>Detalhes do Ambiente</h3>
          <DetailRow label="Imagem Docker" value={ws.image || '—'} mono />
          <DetailRow label="Namespace K8s" value={k8s.namespace || '—'} mono />
          <DetailRow label="Node" value={pod.nodeName || '—'} />
          <DetailRow label="Pod" value={pod.name || '—'} mono />
          <DetailRow label="Uptime" value={k8s.uptime || '—'} />
          <DetailRow label="URL de Acesso" value={ws.workspace_url || '—'} mono />
          <DetailRow label="Criado em" value={ws.created_at ? new Date(ws.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'} />
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: C.text }}>Detalhes do Plano</h3>
          <DetailRow label="Plano" value={planD.name || ws.plan || '—'} />
          <DetailRow label="CPU Limite" value={planD.cpu_limit || '—'} />
          <DetailRow label="Memória Limite" value={planD.memory_limit || '—'} />
          <DetailRow label="Disco" value={planD.storage || '—'} />
          <DetailRow label="Réplicas Máx." value={`${planD.max_replicas || '—'}`} />
          <DetailRow label="Auto-Suspend" value={planD.auto_suspend_enabled ? `${planD.auto_suspend_minutes || 30}min` : 'Sempre ligado'} />
          <DetailRow label="Limite Diário" value={planD.daily_hours_limit ? `${planD.daily_hours_limit}h/dia` : '∞ Ilimitado'} />
          {planD.daily_hours_limit > 0 && details.daily_usage && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ flex: 1, height: 8, background: T.border, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(100, details.daily_usage.percent)}%`, height: '100%', borderRadius: 4,
                    background: details.daily_usage.percent >= 100 ? C.red : details.daily_usage.percent >= 80 ? C.yellow : C.green,
                  }} />
                </div>
                <span style={{ fontSize: 11, color: C.secondary }}>
                  {Math.floor(details.daily_usage.active_minutes / 60)}h{String(details.daily_usage.active_minutes % 60).padStart(2, '0')}m / {details.daily_usage.limit_hours}h
                </span>
              </div>
            </div>
          )}
          <div style={{ marginTop: 16 }}>
            <Button variant="primary" size="sm" onClick={() => route('/upgrade')} fullWidth>⬆Fazer Upgrade</Button>
          </div>
        </div>
      </div>

      {/* ── Token de Acesso ── */}
      {ws.gateway_token && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: C.text }}>Token de Acesso</h3>
          <p style={{ fontSize: 12, color: C.muted, margin: '0 0 12px' }}>
            Use este token para conectar ao ambiente via OpenClaw CLI ou API.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              flex: 1, padding: '10px 14px', background: C.darkBg, border: `1px solid ${C.border}`,
              borderRadius: 8, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: C.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {tokenVisible ? ws.gateway_token : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setTokenVisible(!tokenVisible)}>{tokenVisible ? 'Ocultar' : 'Mostrar'}</Button>
            <Button variant="primary" size="sm" onClick={() => { navigator.clipboard.writeText(ws.gateway_token); showToast('Token copiado!', 'success'); }} icon={<CopyIcon />}>Copiar</Button>
          </div>
        </div>
      )}

      {/* ── Events (collapsible) ── */}
      {eventsExpanded && events.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: C.text }}>Últimos Eventos</h3>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {events.slice().reverse().map((ev: any, i: number) => {
              const evColor = ev.type === 'Warning' ? C.yellow : ev.type === 'Error' ? C.red : C.green;
              const time = ev.lastTimestamp ? new Date(ev.lastTimestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';
              return (
                <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.border}22` }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: evColor, flexShrink: 0, marginTop: 5 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: C.muted }}>{time}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: evColor }}>{ev.reason}</span>
                    </div>
                    <div style={{ fontSize: 12, color: C.secondary, marginTop: 2 }}>{ev.message}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Logs (collapsible) ── */}
      {logsExpanded && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Logs do Container</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" size="xs" onClick={fetchLogs} disabled={logsLoading}>{logsLoading ? '...' : 'Atualizar'}</Button>
              <Button variant="ghost" size="xs" onClick={downloadLogs} icon={<DownloadIcon />}>.txt</Button>
            </div>
          </div>
          <pre ref={logsRef} style={{
            padding: 16, background: C.darkBg, borderRadius: 10,
            color: C.green, fontSize: 11, fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0, minHeight: 200, maxHeight: 400,
            overflow: 'auto', border: `1px solid ${C.border}`,
          }}>
            {logs || 'Clique em "Atualizar" para carregar logs...'}
          </pre>
        </div>
      )}

      {/* ── Confirm Modals ── */}
      {confirmAction === 'restart' && (
        <ConfirmModal title="Reiniciar Ambiente" message={`Tem certeza que deseja reiniciar "${ws.name}"? O ambiente ficará indisponível por alguns segundos.`}
          onConfirm={() => handleAction('restart')} onCancel={() => setConfirmAction(null)} />
      )}
      {confirmAction === 'stop' && (
        <ConfirmModal title="Parar Ambiente" message={`Tem certeza que deseja parar "${ws.name}"? O ambiente ficará offline até ser iniciado novamente.`}
          onConfirm={() => handleAction('stop')} onCancel={() => setConfirmAction(null)} />
      )}
      {confirmAction === 'delete' && (
        <ConfirmModal title="DELETAR PERMANENTEMENTE" message={`Esta ação é IRREVERSÍVEL. Todos os dados de "${ws.name}" serão perdidos. Deseja continuar?`}
          onConfirm={() => handleAction('delete')} onCancel={() => setConfirmAction(null)} danger />
      )}

      <style>{`@keyframes statusPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  );
}
